import { useEffect, useRef } from 'react';
import { useQueryClient, type InfiniteData } from '@tanstack/react-query';
import * as chatSocket from '../../lib/chatSocket';
import * as chatApi from '../../api/chat';
import {
  CHAT_EVENTS,
  type ChatMessage,
  type ChatRoom,
  type ChatUnreadSummary,
  type MessageCreatedPayload,
  type MessageDeletedPayload,
  type MessageUpdatedPayload,
  type ReadPayload,
  type RoomMemberAddedPayload,
  type RoomMemberRemovedPayload,
  type RoomUpdatedPayload,
  type UnreadUpdatedPayload,
} from '../../types/chat';
import { chatKeys } from './chatKeys';
import { useChatAuthToken } from './useChatAuthToken';
import { useCurrentChatIdentity } from './useCurrentChatIdentity';

// Track last-seen ULID per subscribed room so we can fill the gap on
// reconnect via GET /messages?after=…
type LastSeenMap = Map<string, string>;

function patchPages(
  qc: ReturnType<typeof useQueryClient>,
  roomId: string,
  patch: (pages: ChatMessage[][]) => ChatMessage[][]
): void {
  qc.setQueryData<InfiniteData<ChatMessage[]>>(chatKeys.messages(roomId), (prev) => {
    if (!prev) return prev;
    return { ...prev, pages: patch(prev.pages) };
  });
}

/**
 * Global chat socket lifecycle. Mount this once at the app root. It connects
 * the singleton socket whenever the user is authenticated and dispatches
 * incoming server events into the TanStack cache so any consumer hook
 * (`useChatRooms`, `useChatMessages`, `useChatUnread`) stays live without
 * extra refetches.
 */
export function useChatSocket(): void {
  const token = useChatAuthToken();
  const qc = useQueryClient();
  const { userId: selfId } = useCurrentChatIdentity();
  const lastSeenRef = useRef<LastSeenMap>(new Map());

  useEffect(() => {
    if (!token) {
      chatSocket.disconnect();
      return;
    }

    const socket = chatSocket.connect({
      token,
      onConnectError: (err) =>
        console.warn('[chat] socket connect_error:', err.message),
    });

    const onMessageCreated = (payload: MessageCreatedPayload) => {
      const { room_id, message } = payload;
      lastSeenRef.current.set(room_id, message.id);
      patchPages(qc, room_id, (pages) => {
        if (pages.length === 0) return [[message]];
        const head = pages[0];
        if (head.some((m) => m.id === message.id)) return pages;
        return [[message, ...head], ...pages.slice(1)];
      });
      // Bump rooms list (last_message_at order changed) without a refetch:
      // invalidate so the list re-sorts. Active rooms refetch is cheap.
      qc.invalidateQueries({ queryKey: ['chat', 'rooms'] });
      if (message.attachment_ids.length > 0) {
        qc.invalidateQueries({ queryKey: chatKeys.attachments(room_id) });
      }
      // Backend doesn't fan out chat:read to peers, so refresh the room
      // detail when an inbound peer message arrives — they were just in the
      // room and may have advanced their read pointer right before sending.
      // This is what upgrades the local sender's prior messages from ✓ → ✓✓.
      if (selfId && message.sender_id !== selfId) {
        qc.invalidateQueries({ queryKey: chatKeys.room(room_id) });
      }
    };

    const onMessageUpdated = (payload: MessageUpdatedPayload) => {
      const msg = payload.message;
      patchPages(qc, msg.room_id, (pages) =>
        pages.map((p) => p.map((m) => (m.id === msg.id ? msg : m)))
      );
    };

    const onMessageDeleted = (payload: MessageDeletedPayload) => {
      patchPages(qc, payload.room_id, (pages) =>
        pages.map((p) =>
          p.map((m) =>
            m.id === payload.message_id
              ? { ...m, body: null, deleted_at: new Date().toISOString() }
              : m
          )
        )
      );
    };

    const onRoomMemberAdded = (payload: RoomMemberAddedPayload) => {
      qc.invalidateQueries({ queryKey: chatKeys.room(payload.room_id) });
      qc.invalidateQueries({ queryKey: ['chat', 'rooms'] });
    };

    const onRoomMemberRemoved = (payload: RoomMemberRemovedPayload) => {
      qc.invalidateQueries({ queryKey: chatKeys.room(payload.room_id) });
      qc.invalidateQueries({ queryKey: ['chat', 'rooms'] });
    };

    const onRoomUpdated = (payload: RoomUpdatedPayload) => {
      const room: ChatRoom = payload.room;
      qc.setQueryData(chatKeys.room(room.id), room);
      qc.invalidateQueries({ queryKey: ['chat', 'rooms'] });
    };

    const onRead = (payload: ReadPayload) => {
      // Multi-device: my other session marked the room read. Reflect that
      // in unread cache.
      qc.setQueryData<ChatUnreadSummary>(chatKeys.unread(), (prev) => {
        if (!prev) return prev;
        const next = prev.rooms.filter((r) => r.room_id !== payload.room_id);
        const removed = prev.rooms.find((r) => r.room_id === payload.room_id);
        return {
          total_unread: Math.max(0, prev.total_unread - (removed?.unread_count ?? 0)),
          rooms: next,
        };
      });
      // Multi-device: also advance the actor's own read pointer in the
      // room-detail cache so any open thread reflects the new pointer.
      qc.setQueryData<ChatRoom>(chatKeys.room(payload.room_id), (prev) => {
        if (!prev || !selfId) return prev;
        const nowIso = new Date().toISOString();
        return {
          ...prev,
          last_read_message_id: payload.last_read_message_id,
          last_read_at: nowIso,
          unread_count: 0,
          members: prev.members.map((m) =>
            m.user_id === selfId
              ? {
                  ...m,
                  last_read_message_id: payload.last_read_message_id,
                  last_read_at: nowIso,
                }
              : m
          ),
        };
      });
    };

    const onUnreadUpdated = (payload: UnreadUpdatedPayload) => {
      qc.setQueryData<ChatUnreadSummary>(chatKeys.unread(), (prev) => {
        const others = (prev?.rooms ?? []).filter((r) => r.room_id !== payload.room_id);
        return {
          total_unread: payload.total_unread,
          rooms:
            payload.unread_count > 0
              ? [
                  {
                    room_id: payload.room_id,
                    unread_count: payload.unread_count,
                    latest_unread_preview: payload.latest_unread_preview,
                  },
                  ...others,
                ]
              : others,
        };
      });
    };

    socket.on(CHAT_EVENTS.MESSAGE_CREATED, onMessageCreated);
    socket.on(CHAT_EVENTS.MESSAGE_UPDATED, onMessageUpdated);
    socket.on(CHAT_EVENTS.MESSAGE_DELETED, onMessageDeleted);
    socket.on(CHAT_EVENTS.ROOM_MEMBER_ADDED, onRoomMemberAdded);
    socket.on(CHAT_EVENTS.ROOM_MEMBER_REMOVED, onRoomMemberRemoved);
    socket.on(CHAT_EVENTS.ROOM_UPDATED, onRoomUpdated);
    socket.on(CHAT_EVENTS.READ, onRead);
    socket.on(CHAT_EVENTS.UNREAD_UPDATED, onUnreadUpdated);

    // Reconnect handler: fill any gap between the last-seen ULID and the
    // freshly opened socket for every subscribed room.
    const onConnect = async () => {
      for (const [roomId, lastId] of lastSeenRef.current.entries()) {
        try {
          const newer = await chatApi.listMessages(roomId, { after: lastId, limit: 200 });
          for (const message of newer) {
            onMessageCreated({ room_id: roomId, message });
          }
        } catch {
          // The next tick of any active query will refetch this room.
        }
      }
      qc.invalidateQueries({ queryKey: ['chat', 'rooms'] });
      qc.invalidateQueries({ queryKey: chatKeys.unread() });
    };
    socket.on('connect', onConnect);

    return () => {
      socket.off(CHAT_EVENTS.MESSAGE_CREATED, onMessageCreated);
      socket.off(CHAT_EVENTS.MESSAGE_UPDATED, onMessageUpdated);
      socket.off(CHAT_EVENTS.MESSAGE_DELETED, onMessageDeleted);
      socket.off(CHAT_EVENTS.ROOM_MEMBER_ADDED, onRoomMemberAdded);
      socket.off(CHAT_EVENTS.ROOM_MEMBER_REMOVED, onRoomMemberRemoved);
      socket.off(CHAT_EVENTS.ROOM_UPDATED, onRoomUpdated);
      socket.off(CHAT_EVENTS.READ, onRead);
      socket.off(CHAT_EVENTS.UNREAD_UPDATED, onUnreadUpdated);
      socket.off('connect', onConnect);
    };
  }, [token, qc, selfId]);
}

/**
 * Subscribe to a room's live channel for the duration of the component's
 * lifetime. Re-subscribes automatically across reconnects (handled in
 * `chatSocket`'s singleton).
 */
export function useChatRoomSubscription(roomId: string | null | undefined): void {
  useEffect(() => {
    if (!roomId) return;
    chatSocket.subscribeRoom(roomId);
    return () => chatSocket.unsubscribeRoom(roomId);
  }, [roomId]);
}
